import { http } from '@/core/api/';
import schoolTypes from '@/utils/constants/student/schoolTypes';
import academicYearApi from '@/modules/common/academicyear.api';
import locationApi from '@/modules/common/location.api';
import { initialPagination } from './search/_utils';
import { GRADES_ENUM } from './_types';

const SCHOOL_TYPES_PARAM = [schoolTypes.PublicSchool, schoolTypes.Institutions];

/**
 * Get current academic year
 * @returns {Promise<number | null>}
 */
export const getCurrentAcademicYear = async () => {
  try {
    const academicYear = await academicYearApi.getCurrentAcademicYear();
    return academicYear
      ? { id: academicYear.id, description: academicYear.description }
      : null;
  } catch {
    return null;
  }
};

/**
 *  Get academic years
 * @returns {Promise} Promise
 */
export const getAcademicYears = async () => {
  try {
    const { data } = await academicYearApi.getAcademicYears();
    return (
      data?.map(({ id, description, selected }) => ({
        id,
        description,
        selected,
      })) ?? []
    );
  } catch {
    return [];
  }
};

/**
 * Get centers
 * @param { Object } query
 * @param { (string | number) } query.academicYearId
 * @returns {Promise} Promise
 */
export const getCenters = async (query) =>
  (
    await locationApi.getCenters({
      ...query,
      schoolTypes: SCHOOL_TYPES_PARAM,
    })
  )?.map(({ id, name }) => ({
    id,
    name,
    academicYearId: query?.academicYearId,
  })) ?? [];

export const getOres = async () =>
  (await locationApi.getOres())?.map(({ id, name }) => ({
    id,
    name,
  })) ?? [];

/**
 * Get municipalities
 * @param { Object } query
 * @param { (string | number) } query.academicYearId
 * @param { (string | number | undefined) } query.centerId
 * @returns {Promise} Promise
 */
export const getMunicipalities = async (query) => {
  return (
    (
      await locationApi.getMunicipalities({
        ...query,
        schoolTypes: SCHOOL_TYPES_PARAM,
      })
    )?.map(({ id, name }) => ({
      id,
      name,
      centerId: query.centerId,
    })) ?? []
  );
};

/**
 * Get schools
 * @param { Object } query
 * @param { (string | number) } query.academicYearId
 * @param { (string | number | undefined) } query.regionId
 * @param { (string | number | undefined) } query.municipalityId
 * @returns {Promise} Promise
 */
export const getSchools = async (query) => {
  return (
    (
      await locationApi.getSchools({
        ...query,
        schoolTypes: SCHOOL_TYPES_PARAM,
      })
    )?.map(({ id, name }) => ({
      id,
      name,
      municipalityId: query.municipalityId,
    })) ?? []
  );
};

/**
 *  Get categories
 * @returns {Promise} Promise
 */
export const getCategories = async () => {
  try {
    const url = 'api/tapAlternativeLocations/types';
    const { data } = await http.get(url);
    return (
      data?.map(({ id, name }) => ({
        id,
        name,
      })) ?? []
    );
  } catch {
    return [];
  }
};

/**
 *  Get tapAlternativeLocations document types
 * @returns {Promise} Promise
 */
export const getDocumentTypesByCategory = async (category) => {
  try {
    const url = `api/tapAlternativeLocations/documenttypes/${category}`;
    const { data } = await http.get(url);
    return data ?? [];
  } catch {
    return [];
  }
};

/**
 *  Get salon specialties
 * @returns {Promise} Promise
 */
export const getSalonSpecialties = async () => {
  try {
    const url = 'api/code/salonspecialties';
    const { data } = await http.get(url);
    return (
      data?.map(({ id, name, code, title }) => ({
        id,
        name,
        code,
        title,
      })) ?? []
    );
  } catch {
    return [];
  }
};

/**
 *  Get salon specialties by alternative
 * @returns {Promise} Promise
 */
export const getSalonSpecialtiesByAlternative = async (alternativeId) => {
  try {
    const url = `api/tapAlternativeLocations/${alternativeId}/salonspecialties`;
    const { data } = await http.get(url);
    return data ?? [];
  } catch {
    return [];
  }
};

/**
 *  Get salon specialties
 * @returns {Promise} Promise
 */
export const getStudentCapacityChangeReasons = async () => {
  try {
    const url = 'api/code/studentcapacitychangereasons';
    const { data } = await http.get(url);
    return (
      data?.map(({ id, name }) => ({
        id,
        name,
      })) ?? []
    );
  } catch {
    return [];
  }
};

/**
 *  Get Student Capacity
 * @returns {Promise} Promise
 */
export const getStudentCapacity = async (schoolAlternativeLocationId) => {
  try {
    const url = `api/tapAlternativeLocations/${schoolAlternativeLocationId}/studentcapacity`;
    const { data } = await http.get(url);
    return data;
  } catch {
    return null;
  }
};

/**
 *  Get Student Capacity Limit
 * @returns {Promise} Promise
 */
export const getStudentCapacityLimit = async (schoolAlternativeLocationId) => {
  try {
    const url = `api/tapAlternativeLocations/${schoolAlternativeLocationId}/studentcapacitylimit`;
    const { data } = await http.get(url);
    return data;
  } catch {
    return null;
  }
};

/**
 *  Update Student Capacity
 * @returns {Promise} Promise
 */
export const updateStudentCapacity = async (
  schoolAlternativeLocationId,
  payload
) => {
  const url = `api/tapAlternativeLocations/${schoolAlternativeLocationId}/studentcapacity`;
  return http.put(url, payload);
};

/**
 * Get names
 * @param { Object } query
 * @param { (string | number) } query.categoryId
 * @returns {Promise} Promise
 */
export const getNames = async (query) => {
  try {
    const url = `api/tapAlternativeLocations/names/${query.categoryId}`;
    const { data } = await http.get(url, {
      params: { academicYearId: query.academicYearId },
    });
    return (
      data?.map(({ id, name, studentLimit }) => ({
        id,
        name,
        categoryId: query.categoryId,
        studentLimit,
      })) ?? []
    );
  } catch {
    return [];
  }
};

/**
 * Get teachers
 * @param { Object } query
 * @param { (string | number) } query.academicYearId
 * * @param { (string | number) } query.categoryId
 * * @param { (string | number) } query.subcategoryId
 * @returns {Promise} Promise
 */
export const getTeachers = async (query) => {
  try {
    const url = `api/tapAlternativeLocations/teachers`;
    const { data } = await http.get(url, { params: query });
    return data;
  } catch {
    return [];
  }
};

export const getTeachersBySchool = async (academicYearId, schoolId) => {
  try {
    const url = `api/tapAlternativeLocations/teachers/${academicYearId}/${schoolId}`;
    const { data } = await http.get(url);
    return data;
  } catch {
    return [];
  }
};

/**
 * Get grades
 * @param { Object } query
 * @returns {Promise} Promise
 */
export const getGrades = async () => {
  try {
    const url = 'api/books/grades';
    const { data } = await http.get(url);
    return (
      data?.map(({ id, name }) => ({
        id,
        name: GRADES_ENUM[name] || name,
      })) ?? []
    );
  } catch {
    return [];
  }
};

/**
 * Search Location Alternatives
 * @param {Object} query
 * @param { (string | number) } query.academicYearId
 * @param { (string | number | undefined) } query.centerId
 * @param { (string | number | undefined) } query.municipalityId
 * @param { (string | number | undefined) } query.schoolId
 * @param { (string | number | undefined) } query.categoryId
 * @param { (string | number | undefined) } query.nameId
 * @param { (string | number | undefined) } query.gradeId
 * @param {number | undefined} query.pageIndex
 * @param {number | undefined} query.pageSize
 * @returns {Promise<SearchResult>} Promise<SearchResult>
 */
export const searchLocationAlternatives = async (query) => {
  const empty = { data: [], ...initialPagination() };
  try {
    const url = 'api/tapAlternativeLocations/search';
    const { data } = await http.get(url, { params: query });
    return data ?? empty;
  } catch {
    return empty;
  }
};

/**
 * Get Location Alternative
 * @param {Object} query
 * @param {number} query.schoolId
 * @param {number} query.academicYearId
 * @returns {Promise<LocationAlternative | null>} LocationAlternative
 */
export const getLocationAlternative = async ({ schoolId, academicYearId }) => {
  try {
    const url = `api/tapAlternativeLocations/${schoolId}/${academicYearId}`;
    const { data } = await http.get(url);
    return locationAlternativeWithGradeDescription(data);
  } catch {
    return null;
  }
};

/**
 * Update Location Alternative
 * @param {Object} query
 * @param {number} query.schoolId
 * @param {number} query.academicYearId
 * @param {Object} body
 * @param {Alternative[]} body.alternatives
 * @returns {Promise<DataResponse>} LocationAlternative
 */
export const updateLocationAlternative = async (query, body) => {
  try {
    const { schoolId, academicYearId } = query;
    const url = `api/tapAlternativeLocations/${schoolId}/${academicYearId}`;
    const { data } = await http.put(url, body);
    return {
      status: true,
      data: locationAlternativeWithGradeDescription(data),
    };
  } catch (e) {
    return { status: false, data: e };
  }
};

export const deleteAlternativeLocation = async (id) => {
  try {
    const url = `api/tapAlternativeLocations/delete/${id}`;
    await http.put(url);
    return {
      status: true,
    };
  } catch (e) {
    return { status: false, data: e };
  }
};

/**
 *  Get determination by alternative
 * @returns {Promise} Promise
 */
export const getDetermination = async (alternativeId) => {
  return await http.get(
    `api/tapAlternativeLocations/${alternativeId}/determination`
  );
};

/**
 *  Save determination by alternative
 * @returns {Promise} Promise
 */

export const saveDetermination = async (alternativeId, payload) => {
  return await http.post(
    `api/tapAlternativeLocations/${alternativeId}/determination`,
    payload
  );
};

export const getAlternativeLocationActionPlan = async (
  schoolAlternativeLocationId,
  academicYearId
) =>
  await http.get(
    `api/tapAlternativeLocations/actionplan/${schoolAlternativeLocationId}/${academicYearId}`
  );

export const getAlternativeLocationActionPlanById = async (id) =>
  await http.get(`api/tapAlternativeLocations/actionplan/${id}`);

export const searchAlternativeLocationActionPlanStudents = async (
  studentSieIdOrRequestNumber,
  academicYearId
) =>
  await http.get('api/tapAlternativeLocations/actionplan/searchstudent', {
    params: {
      studentSieIdOrRequestNumber,
      academicYearId,
    },
  });

export const saveAlternativeLocationActionPlan = async (payload) =>
  await http.post('api/tapAlternativeLocations/actionplan/save', payload);

export const saveAlternativeLocationActionPlanStudent = async (
  actionPlanId,
  payload
) =>
  await http.post(
    'api/tapAlternativeLocations/actionplan/savestudent',
    payload,
    {
      params: {
        actionPlanId,
      },
    }
  );

export const updateActionPlanStudentCount = async (actionPlanId, studentCount) =>
  await http.put(
    `api/tapAlternativeLocations/actionplan/${actionPlanId}/studentcount`,
    studentCount
  );

export const removeAlternativeLocationActionPlanStudent = async (
  actionPlanId,
  studentSieId
) =>
  await http.delete('api/tapAlternativeLocations/actionplan/removestudent', {
    params: {
      actionPlanId,
      studentSieId,
    },
  });

// utils

const locationAlternativeWithGradeDescription = (data) =>
  data
    ? {
        ...data,
        availableGrades:
          data.availableGrades?.map((ag) => ({
            id: ag.id,
            name: GRADES_ENUM[ag.name] || ag.name,
          })) ?? [],
        alternatives:
          data.alternatives?.map((a) => ({
            ...a,
            grades:
              a.grades?.map((g) => ({
                id: g.id,
                name: GRADES_ENUM[g.name] || g.name,
              })) ?? [],
          })) ?? [],
      }
    : null;

// typedefs

/**
 * @typedef {Object} KeyValuePair
 * @property {number} id
 * @property {string} name
 */

/**
 * @typedef {Object} SearchResult
 * @property {SearchResultItem[]} data
 * @property {number} totalItemCount
 * @property {number} currentPage
 * @property {number} count
 * @property {number} pageCount
 */

/**
 * @typedef {Object} SearchResultItem
 * @property {KeyValuePair} academicYear
 * @property {KeyValuePair} center
 * @property {KeyValuePair} municipality
 * @property {KeyValuePair} school
 */

/**
 * @typedef {Object} LocationAlternative
 * @property {number} id
 * @property {KeyValuePair} academicYear
 * @property {KeyValuePair} center
 * @property {KeyValuePair} municipality
 * @property {KeyValuePair} school
 * @property {KeyValuePair[]} availableGrades
 * @property {Alternative[]} alternatives
 */

/**
 * @typedef {Object} Alternative
 * @property {number} id
 * @property {KeyValuePair} category
 * @property {KeyValuePair} name
 * @property {string} description
 * @property {KeyValuePair[]} grades
 * @property {boolean} active
 */

/**
 * @typedef {Object} DataResponse
 * @property {boolean} status
 * @property {LocationAlternative} data
 */
